import { useRef, useState, useEffect, useMemo } from "react";
import type { Attachment } from "../../types";
import playIcon from "../../assets/icon-video-play.svg";
import pauseIcon from "../../assets/icon-video-pause.svg";
import {
  MediaController,
  MediaControlBar,
  MediaTimeRange,
  MediaTimeDisplay,
  MediaPlayButton,
  MediaMuteButton,
  MediaFullscreenButton,
} from "media-chrome/react";
import { cn } from "../../utils/cn";
import s from "./index.module.css";

interface VideoPlayerProps {
  attachment: Attachment;
  isLandscape: boolean;
}

const getVideoErrorDetails = (mediaError: MediaError | null) => {
  if (!mediaError) {
    return {
      type: "UNKNOWN",
      message: "An unknown error occurred while playing the video",
    };
  }

  switch (mediaError.code) {
    case MediaError.MEDIA_ERR_ABORTED:
      return {
        type: "ABORTED",
        message: "The video loading was aborted",
      };
    case MediaError.MEDIA_ERR_NETWORK:
      return {
        type: "NETWORK",
        message: "A network error occurred while loading the video",
      };
    case MediaError.MEDIA_ERR_DECODE:
      return {
        type: "DECODE",
        message: "Video decoding failed, possibly due to unsupported format",
      };
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return {
        type: "NOT_SUPPORTED",
        message: "The video format is not supported or the file is corrupted",
      };
    default:
      return {
        type: "UNKNOWN",
        message: "An unknown error occurred while playing the video",
      };
  }
};

export default function VideoPlayer({ attachment, isLandscape }: VideoPlayerProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState<boolean>(false);
  const [error, setError] = useState<{ type: string; message: string } | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Reset when source changes
  useEffect(() => {
    setHasStartedPlaying(false);
    setError(null);
    setLoading(false);

    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
      video.load();
    }
  }, [attachment.url]);

  const handleLoadStart = () => {
    setLoading(true);
    setError(null);
  };

  const handleLoadedMetadata = () => {
    setLoading(false);
    setError(null);
  };

  const handleCanPlay = () => {
    setLoading(false);
  };

  const handleWaiting = () => {
    setLoading(true);
  };

  const handlePlaying = () => {
    setHasStartedPlaying(true);
    setLoading(false);
  };

  const handleVideoError = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    const details = getVideoErrorDetails(event.currentTarget.error);
    setLoading(false);
    setError(details);

    console.error("Video playback error:", {
      code: event.currentTarget.error?.code,
      message: event.currentTarget.error?.message,
      url: attachment.url,
    });
  };

  const handleRetry = () => {
    setError(null);
    setHasStartedPlaying(false);
    setLoading(false);

    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
      video.load();
    }
  };

  const posterUrl = useMemo((): string | undefined => {
    if (error) return undefined;
    return `${attachment.url}?x-oss-process=video/snapshot,t_0,f_jpg,w_0,h_0,ar_auto,q_80`;
  }, [attachment.url, error, isLandscape]);

  return (
    <div className={cn("w-full relative", isLandscape ? "h-full p-[1.88vw]" : "aspect-video max-w-lg mx-auto px-8 mb-12")}>
      {error ? (
        <div
          className={cn(
            "w-full h-full bg-black/10 flex flex-col items-center justify-center p-6 text-center",
            isLandscape ? "rounded-[2.8vw]" : "rounded-2xl"
          )}
        >
          <h3 className="mb-2 text-base font-semibold text-[#E63232]">
            Video loading failed
          </h3>
          <p className="mb-4 text-sm text-[#333333] max-w-xs">
            {error.message}
          </p>
          <button
            type="button"
            onClick={handleRetry}
            className="inline-flex items-center justify-center rounded-full bg-[#1B1B1B]/90 px-6 py-2 text-sm font-semibold text-white transition hover:bg-[#1B1B1B]"
          >
            Retry
          </button>
        </div>
      ) : (
        <div
          className={cn(
            "relative h-full w-full overflow-hidden bg-black",
            isLandscape ? "rounded-[2.8vw]" : "rounded-2xl"
          )}
        >
          {loading && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 text-white">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/50 border-t-white" />
            </div>
          )}

          <MediaController className="relative z-0 h-full w-full" autohide="1.5">
            <video
              slot="media"
              id="video"
              ref={videoRef}
              src={attachment.url}
              controls={false}
              playsInline
              webkit-playsinline="true"
              x5-playsinline="true"
              preload="auto"
              poster={posterUrl ?? undefined}
              className="z-0 h-full w-full object-contain"
              onLoadStart={handleLoadStart}
              onLoadedMetadata={handleLoadedMetadata}
              onCanPlay={handleCanPlay}
              onWaiting={handleWaiting}
              onPlaying={handlePlaying}
              onError={handleVideoError}
            >
              Your browser does not support the video element.
            </video>

            {!loading && (
              <MediaPlayButton
                slot="centered-chrome"
                noTooltip={true}
                style={{
                  width: "60px",
                  height: "60px",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  padding: "0",
                  borderRadius: "50%",
                  border: "none",
                }}
              >
                <img slot="play" src={playIcon} alt="" className="h-10 w-10 object-contain" />
                <img slot="pause" src={pauseIcon} alt="" className="h-8 w-8 object-contain" />
              </MediaPlayButton>
            )}

            {hasStartedPlaying && (
              <MediaControlBar className={s["media-control-bar"]}>
                <MediaMuteButton noTooltip={true} style={{ width: isLandscape ? "auto" : "40px" }} />
                <MediaTimeRange />
                <MediaTimeDisplay showDuration style={{ padding: "5px" }} />
                <MediaFullscreenButton noTooltip={true} style={{ width: isLandscape ? "auto" : "40px" }} />
              </MediaControlBar>
            )}
          </MediaController>
        </div>
      )}
    </div>
  );
}
